import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { FixedSizeList as List } from 'react-window';
import { v4 as uuidv4 } from "uuid";
import CommandCenterService from '../../../../../../service/CommandCenterService';
import useUrlParams from '../../../../hooks/useUrlParams';
import { getEnvVar } from '../../../../../../envUtils';
import { get_formated_time } from '../../../../utils';
import useScenario from '../../../../hooks/useScenario';
import './Console.scss';
import { ArrowUp } from './ConsoleSvg';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';

// Fetches command details from the server based on provided parameters and cursor.
const fetchEntries = async (sat_id, truetwin_id, scenario_id, cursor) => {
    try {
        const { data } = await CommandCenterService.getAllRawCommandDetails(
            sat_id,
            truetwin_id,
            scenario_id,
            cursor,
            50
        );
        return {
            data: data || [],
            cursor: data?.[0]?.cursorId || ''
        };
    } catch (error) {
        console.error('Fetch error:', error);
        return { data: [], cursor: '' };
    }
};

// Memoized component to display the list of console output entries.
const RawOuputList = memo(({ height, show_selected_output_details, selectedRow }) => {
    const { sat_id, truetwin_id, scenario_id } = useUrlParams(); // Get parameters from the custom hook
    const { scenario } = useScenario()
    const [entries, setEntries] = useState([]);
    const listRef = useRef(null);
    const loadingRef = useRef(false);
    const cursorRef = useRef('');
    const isAtTopRef = useRef(true);
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Loads the latest console entries from the server.
    const loadLatestEntries = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        try {
            const { data, cursor } = await fetchEntries(sat_id, truetwin_id, scenario_id, '');
            setEntries(data);
            cursorRef.current = cursor;
            show_selected_output_details(null);
            if (isAtTopRef.current) listRef.current?.scrollToItem(0, 'start');
        } finally {
            loadingRef.current = false;
        }
    }, [sat_id, truetwin_id, scenario_id]);

    // Loads more console entries from the server using the current cursor.
    const loadMoreEntries = useCallback(async () => {
        if (loadingRef.current || !cursorRef.current) return;
        loadingRef.current = true;
        try {
            const { data, cursor } = await fetchEntries(sat_id, truetwin_id, scenario_id, cursorRef.current);
            setEntries(prev => [...prev, ...data]);
            cursorRef.current = cursor;
        } finally {
            loadingRef.current = false;
        }
    }, [sat_id, truetwin_id, scenario_id]);

    // Handles scroll events from the list and loads more entries when needed.
    const handleScroll = useCallback(({ scrollOffset, scrollDirection }) => {
        setShowScrollTop(scrollOffset > 10);
        const list = listRef.current;
        if (!list) return;

        const totalHeight = entries.length * list.props.itemSize;
        const bottomThreshold = 5;

        if (scrollDirection === 'forward' && scrollOffset + list.props.height >= totalHeight - bottomThreshold) {
            loadMoreEntries();
        } else if (scrollDirection === 'backward' && scrollOffset === 0) {
            loadLatestEntries();
        }
        isAtTopRef.current = scrollOffset === 0;
    }, [entries?.length, loadMoreEntries, loadLatestEntries]);

    // Establishes a WebSocket connection to receive real-time updates and load latest entries.
    useEffect(() => {
        const token = sessionStorage.getItem("authentication");
        if (!truetwin_id || !token) return;

        const sessionId = uuidv4();
        const ws = new WebSocket(`${getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL')}groundstation/websocketstatus?truetwinId=${truetwin_id}&sessionId=${sessionId}`);

        ws.onopen = () => ws.send(JSON.stringify({ type: 'authorization', token }));
        ws.onmessage = () => isAtTopRef.current && loadLatestEntries();
        ws.onerror = console.error;

        return () => ws.close();
    }, [truetwin_id, loadLatestEntries]);

    // Loads the latest entries when the component mounts.
    useEffect(() => { loadLatestEntries(); }, [loadLatestEntries]);

    const base64ToHex = useCallback((str) => {
        const raw = atob(str);
        let result = '';
        for (let i = 0; i < raw.length; i++) {
            const hex = raw.charCodeAt(i).toString(16).padStart(2, '0');
            result += `${hex} `;
        }
        return result.toUpperCase();
    }, []);

    // Memoized component to render a single row in the list.
    const Row = memo(({ index, style }) => {
        const entry = entries[index];
        const isSelected = entry?.CommandUid === selectedRow?.CommandUid;

        return (
            <div style={style}>
                <div className={`table__body__row d-flex`}>
                    <button
                        className={`table__body__row__item gap-3 ${isSelected ? 'selected' : ''}`}
                        onClick={() => show_selected_output_details(entry)}
                        style={{ borderLeft: isSelected ? "2px solid #CCF54E" : undefined }}
                    >
                        <TableCell
                            width={160}
                            content={get_formated_time(entry?.CommandDetails?.groundTimestamp / 1000)}
                        />
                        <TableCell
                            width={850}
                            content={base64ToHex(entry?.CommandDetails?.completeFrame || '')}
                        />
                    </button>
                </div>
            </div>
        );
    });

    return (
        <>
            <List
                ref={listRef}
                height={height}
                itemCount={entries?.length}
                itemSize={35}
                width="100%"
                onScroll={handleScroll}
                overscanCount={5}
            >
                {Row}
            </List>
            <button
                onClick={() => {
                    listRef.current?.scrollToItem(0, 'start');
                }}
                title="Scroll to top"
                className={`scroll-to-top ${showScrollTop ? '' : 'hidden'}`}
            >
                <ArrowUp />
            </button>
        </>
    );
});

const TableCell = memo(({ width, content, children }) => (
    <div className="d-flex align-items-center justify-content-start" style={{ width: `${width}px` }}>
        <CustomToolTip title={content} placement="top">
            <span className="truncate__text">{children ?? content}</span>
        </CustomToolTip>
    </div>
));
export default RawOuputList;